import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import api from '../../services/api';

const MAX_BYTES = 6 * 1024 * 1024; // 6 MB

const formatSize = b => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;

const compressImage = (file) =>
  new Promise(resolve => {
    if (file.size <= MAX_BYTES) { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let w = img.naturalWidth, h = img.naturalHeight;
      const MAX_DIM = 4096;
      if (w > MAX_DIM || h > MAX_DIM) {
        const r = Math.min(MAX_DIM / w, MAX_DIM / h);
        w = Math.round(w * r); h = Math.round(h * r);
      }
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const attempt = q => {
        canvas.toBlob(blob => {
          if (!blob) { resolve(file); return; }
          if (blob.size <= MAX_BYTES || q <= 0.1) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg', lastModified: Date.now() }));
          } else {
            attempt(Math.round((q - 0.1) * 10) / 10);
          }
        }, 'image/jpeg', q);
      };
      attempt(0.9);
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });

const CATEGORIES = [
  { key: 'plant', label: 'Plant & Facility' },
  { key: 'products', label: 'Products' },
  { key: 'team', label: 'Our Team' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'certifications', label: 'Certifications' },
  { key: 'events', label: 'Events' },
];

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [filterCat, setFilterCat] = useState('all');
  const [category, setCategory] = useState('plant');
  const [files, setFiles] = useState([]); // [{ file, preview, name, status }]
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/gallery/manage')
      .then(r => { setImages(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const addFiles = async (rawFiles) => {
    const imageFiles = Array.from(rawFiles).filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) { toast.error('Only image files are allowed'); return; }

    const tempEntries = imageFiles.map((f, i) => ({
      _id: Date.now() + i,
      file: f,
      originalSize: f.size,
      compressedSize: null,
      preview: URL.createObjectURL(f),
      name: f.name.replace(/\.[^.]+$/, ''),
      status: f.size > MAX_BYTES ? 'compressing' : 'pending',
    }));
    setFiles(prev => [...prev, ...tempEntries]);

    for (const entry of tempEntries) {
      if (entry.file.size > MAX_BYTES) {
        const compressed = await compressImage(entry.file);
        const newPreview = URL.createObjectURL(compressed);
        URL.revokeObjectURL(entry.preview);
        setFiles(prev => prev.map(f =>
          f._id === entry._id
            ? { ...f, file: compressed, preview: newPreview, compressedSize: compressed.size, status: 'pending' }
            : f
        ));
        toast.success(`${entry.name}: compressed from ${formatSize(entry.originalSize)} → ${formatSize(compressed.size)}`);
      }
    }
  };

  const handleFileInput = e => { addFiles(e.target.files); if (fileRef.current) fileRef.current.value = ''; };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = idx => {
    setFiles(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };

  const updateFileName = (idx, val) => setFiles(prev => prev.map((f, i) => i === idx ? { ...f, name: val } : f));

  const handleUpload = async e => {
    e.preventDefault();
    if (!files.length) { toast.error('Please select at least one image'); return; }
    const pending = files.filter(f => f.status === 'pending');
    if (!pending.length) { toast.error('All files already uploaded'); return; }

    setUploading(true);
    setUploadProgress({ done: 0, total: pending.length });
    let doneCount = 0;

    for (let i = 0; i < files.length; i++) {
      if (files[i].status !== 'pending') continue;
      setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f));
      const fd = new FormData();
      fd.append('image', files[i].file);
      fd.append('title', files[i].name);
      fd.append('category', category);
      try {
        await api.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'done' } : f));
        doneCount++;
        setUploadProgress({ done: doneCount, total: pending.length });
      } catch {
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error' } : f));
      }
    }

    toast.success(`${doneCount} of ${pending.length} image${pending.length > 1 ? 's' : ''} uploaded`);
    setUploading(false);
    load();
  };

  const clearDone = () => {
    setFiles(prev => prev.filter(f => f.status !== 'done'));
  };

  const toggleActive = async (img) => {
    try {
      await api.put(`/gallery/${img.id}`, { ...img, is_active: !img.is_active });
      toast.success(img.is_active ? 'Hidden from gallery' : 'Shown in gallery');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image permanently?')) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success('Image deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = filterCat === 'all' ? images : images.filter(i => i.category === filterCat);
  const catLabel = key => CATEGORIES.find(c => c.key === key)?.label || key;
  const pendingCount = files.filter(f => f.status === 'pending').length;
  const doneCount = files.filter(f => f.status === 'done').length;
  const compressingCount = files.filter(f => f.status === 'compressing').length;

  const statusColor = { compressing: '#d97706', pending: '#6b7280', uploading: '#0ea5e9', done: '#16a34a', error: '#dc2626' };
  const statusLabel = { compressing: 'Compressing…', pending: 'Ready', uploading: 'Uploading…', done: 'Uploaded', error: 'Failed' };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Gallery Management</h1>
        <span style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>{images.length} total images</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '1.5rem', alignItems: 'flex-start' }}>

        {/* Upload Form */}
        <div className="card" style={{ position: 'sticky', top: '80px' }}>
          <div className="card-header">
            <h3 className="card-title">Upload Images</h3>
            {files.length > 0 && (
              <span style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>
                {files.length} file{files.length > 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="card-body">
            <form onSubmit={handleUpload}>

              {/* Drop zone */}
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${dragOver ? 'var(--blue)' : 'var(--border)'}`,
                  borderRadius: '10px',
                  padding: '1.75rem 1rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: '1rem',
                  background: dragOver ? '#eff6ff' : '#fafafa',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>&#x1F4C2;</div>
                <p style={{ fontWeight: 700, color: '#374151', marginBottom: '0.2rem', fontSize: '0.9rem' }}>
                  Click to browse or drag & drop
                </p>
                <p style={{ fontSize: '0.78rem', color: 'var(--gray)' }}>
                  JPG, PNG, WEBP — <strong>auto-compressed to 6MB</strong> — multiple files allowed
                </p>
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFileInput} style={{ display: 'none' }} />
              </div>

              {/* Category */}
              <div className="form-group">
                <label className="form-label">Category for all images *</label>
                <Select
                  options={CATEGORIES.map(c => ({ value: c.key, label: c.label }))}
                  value={{ value: category, label: CATEGORIES.find(c => c.key === category)?.label }}
                  onChange={opt => setCategory(opt.value)}
                  isSearchable={false}
                  styles={{
                    control: base => ({ ...base, borderColor: 'var(--border)', boxShadow: 'none', '&:hover': { borderColor: 'var(--blue)' }, fontSize: '0.9rem' }),
                    option: (base, state) => ({ ...base, fontSize: '0.9rem', background: state.isSelected ? 'var(--blue)' : state.isFocused ? '#eff6ff' : 'white', color: state.isSelected ? 'white' : '#1e293b' }),
                  }}
                />
              </div>

              {/* File previews */}
              {files.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label className="form-label" style={{ margin: 0 }}>Selected Files</label>
                    {doneCount > 0 && (
                      <button type="button" onClick={clearDone} style={{ fontSize: '0.75rem', color: 'var(--gray)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                        Clear uploaded
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.25rem' }}>
                    {files.map((f, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', background: f.status === 'done' ? '#f0fdf4' : f.status === 'error' ? '#fef2f2' : '#f8fafc', borderRadius: '8px', padding: '0.5rem', border: `1px solid ${f.status === 'done' ? '#bbf7d0' : f.status === 'error' ? '#fecaca' : 'var(--border)'}` }}>
                        <img src={f.preview} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <input
                            value={f.name}
                            onChange={e => updateFileName(idx, e.target.value)}
                            disabled={f.status !== 'pending'}
                            style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '0.8125rem', fontWeight: 600, color: '#1e293b', outline: 'none', padding: 0 }}
                            placeholder="Image title..."
                          />
                          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: statusColor[f.status], marginTop: '0.1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            {(f.status === 'uploading' || f.status === 'compressing') && <span className="spinner" style={{ width: 10, height: 10, borderWidth: 2, display: 'inline-block', verticalAlign: 'middle' }} />}
                            {statusLabel[f.status]}
                            {f.compressedSize
                              ? <span style={{ color: '#16a34a', fontWeight: 600 }}>({formatSize(f.originalSize)} → {formatSize(f.compressedSize)})</span>
                              : <span style={{ color: '#9ca3af', fontWeight: 500 }}>({formatSize(f.originalSize)})</span>
                            }
                          </div>
                        </div>
                        {f.status === 'pending' && (
                          <button type="button" onClick={() => removeFile(idx)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1rem', padding: '0.1rem', lineHeight: 1, flexShrink: 0 }}>
                            &times;
                          </button>
                        )}
                        {f.status === 'done' && <span style={{ color: '#16a34a', fontSize: '1.1rem', flexShrink: 0 }}>&#10003;</span>}
                        {f.status === 'error' && <span style={{ color: '#dc2626', fontSize: '1.1rem', flexShrink: 0 }}>&#10007;</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress bar */}
              {uploading && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--gray)', marginBottom: '0.3rem' }}>
                    <span>Uploading...</span>
                    <span>{uploadProgress.done} / {uploadProgress.total}</span>
                  </div>
                  <div style={{ height: 6, background: '#e2e8f0', borderRadius: '9999px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'var(--blue)', borderRadius: '9999px', width: `${uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 0}%`, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={uploading || !pendingCount || compressingCount > 0}
                style={{ width: '100%', justifyContent: 'center', padding: '0.65rem', fontSize: '0.9375rem' }}
              >
                {uploading
                  ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> &nbsp;Uploading {uploadProgress.done}/{uploadProgress.total}...</>
                  : compressingCount > 0 ? `Compressing ${compressingCount} image${compressingCount > 1 ? 's' : ''}…`
                  : `Upload ${pendingCount ? pendingCount + ' Image' + (pendingCount > 1 ? 's' : '') : 'Images'}`
                }
              </button>

            </form>
          </div>
        </div>

        {/* Gallery Grid */}
        <div>

          {/* Horizontal category tabs */}
          <div className="card" style={{ padding: '0', marginBottom: '1.5rem', overflow: 'hidden' }}>
            <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid #f1f5f9' }}>
              {[{ key: 'all', label: 'All Photos', count: images.length }, ...CATEGORIES.map(c => ({ key: c.key, label: c.label, count: images.filter(i => i.category === c.key).length }))].map(tab => {
                const active = filterCat === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilterCat(tab.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      padding: '0.875rem 1.25rem',
                      border: 'none',
                      borderBottom: active ? '3px solid #C8171A' : '3px solid transparent',
                      background: 'transparent',
                      color: active ? '#C8171A' : '#64748b',
                      fontWeight: active ? 700 : 500,
                      fontSize: '0.8375rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s',
                      marginBottom: '-1px',
                    }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.color = '#1e293b'; e.currentTarget.style.borderBottomColor = '#e2e8f0'; } }}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderBottomColor = 'transparent'; } }}
                  >
                    {tab.label}
                    <span style={{
                      background: active ? '#C8171A' : '#e2e8f0',
                      color: active ? 'white' : '#64748b',
                      borderRadius: '9999px',
                      padding: '0.1rem 0.5rem',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                    }}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>

          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>&#x1F4F7;</div>
              <p style={{ fontWeight: 600, color: '#374151' }}>No images yet</p>
              <p style={{ fontSize: '0.875rem' }}>Upload images using the form on the left.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
              {filtered.map(img => (
                <div key={img.id} className="card" style={{ overflow: 'hidden', opacity: img.is_active ? 1 : 0.55 }}>
                  <div style={{ height: 160, overflow: 'hidden', position: 'relative' }}>
                    <img src={img.image_path} alt={img.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem' }}>
                      <span style={{ background: 'rgba(0,0,0,0.65)', color: 'white', borderRadius: '6px', padding: '0.15rem 0.5rem', fontSize: '0.68rem', fontWeight: 700 }}>
                        {catLabel(img.category)}
                      </span>
                    </div>
                    {!img.is_active && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ background: 'white', color: '#374151', borderRadius: '6px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 700 }}>Hidden</span>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '0.75rem' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#1e293b', marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {img.title || <span style={{ color: 'var(--gray)', fontStyle: 'italic' }}>No title</span>}
                    </p>
                    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
                      <button
                        onClick={() => toggleActive(img)}
                        className="btn btn-sm"
                        style={{ flex: 1, justifyContent: 'center', background: img.is_active ? '#fef3c7' : '#dcfce7', color: img.is_active ? '#b45309' : '#16a34a', border: 'none', fontSize: '0.75rem' }}
                      >
                        {img.is_active ? 'Hide' : 'Show'}
                      </button>
                      <button onClick={() => handleDelete(img.id)} className="btn btn-sm btn-danger" style={{ flex: 1, justifyContent: 'center', fontSize: '0.75rem' }}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
